import { Link } from "react-router-dom";
import { ArrowRight, WheatOff, ShieldCheck, FlaskConical, Snowflake, MapPin, Recycle, ClipboardCheck, Microscope, FileCheck2 } from "lucide-react";
import { Reveal, MaskedLines } from "../components/store/Reveal";
import { Marquee } from "../components/store/Marquee";

const CERTS = [
  { icon: WheatOff, t: "Certificazione Gluten Free", d: "Ogni piatto contiene meno di 20 ppm di glutine, la soglia fissata dal Regolamento UE 828/2014 per la dicitura \"senza glutine\". Sicuro per celiaci e sensibili al glutine." },
  { icon: FlaskConical, t: "Laboratorio 100% dedicato", d: "Cuciniamo esclusivamente senza glutine: nessuna farina di frumento entra mai nel laboratorio. Zero rischio di contaminazione crociata, dalla dispensa al piatto." },
  { icon: ShieldCheck, t: "Sicurezza alimentare HACCP", d: "Autocontrollo igienico-sanitario secondo il sistema HACCP (Reg. CE 852/2004): punti critici monitorati e registrati in ogni fase di preparazione e abbattimento." },
  { icon: Snowflake, t: "Catena del freddo garantita", d: "Surgelazione rapida a -18°C subito dopo la cottura e trasporto refrigerato monitorato fino a casa tua: gusto, nutrienti e sicurezza intatti." },
  { icon: MapPin, t: "100% Made in Italy", d: "Ricette della tradizione italiana preparate da chef italiani con ingredienti selezionati e filiera tracciata, lotto per lotto." },
  { icon: Recycle, t: "Packaging certificato FSC®", d: "Confezioni in carta kraft da foreste gestite responsabilmente, riciclabili al 100%. La qualità per noi include anche il rispetto del pianeta." },
];

const CONTROLS = [
  { icon: ClipboardCheck, t: "Selezione materie prime", d: "Ogni fornitore è qualificato e ogni ingrediente arriva con certificato di assenza di glutine." },
  { icon: Microscope, t: "Test su ogni lotto", d: "Campionamento e analisi del contenuto di glutine (< 20 ppm) su ogni lotto di produzione." },
  { icon: FileCheck2, t: "Tracciabilità completa", d: "Ogni confezione riporta lotto e scadenza: possiamo risalire a ingredienti e controlli in pochi minuti." },
];

export default function Certificazioni() {
  return (
    <div data-testid="certificazioni-page">
      {/* HERO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 pt-14 lg:pt-24 pb-16 lg:pb-24">
        <div className="max-w-3xl">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-6">Certificazioni & Qualità</p>
          <h1 className="font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-[#1C1917]">
            <MaskedLines lines={["La fiducia si"]} />
            <MaskedLines lines={["costruisce", "con i fatti."]} lineClassName="italic text-[#2E8A57]" startDelay={0.35} />
          </h1>
          <p className="mt-7 text-base md:text-lg text-[#44403C] max-w-xl leading-relaxed">
            Dietro ogni piatto ci sono controlli rigorosi, un laboratorio dedicato al gluten free e certificazioni che garantiscono la tua sicurezza a ogni boccone.
          </p>
        </div>
      </section>

      {/* CERTS GRID */}
      <section className="bg-[#F5EFE5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
          <Reveal>
            <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight max-w-2xl">
              Le nostre garanzie
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E7E0D8] border border-[#E7E0D8] rounded-2xl overflow-hidden">
            {CERTS.map((c, i) => (
              <Reveal key={c.t} delay={(i % 3) * 0.08} className="bg-[#FDFBF7] p-8 group hover:bg-white transition-colors duration-300">
                <div className="w-11 h-11 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-5">
                  <c.icon className="w-5 h-5 text-[#2E8A57]" />
                </div>
                <h3 className="font-serif-display font-bold text-xl text-[#1C1917] group-hover:text-[#2E8A57] transition-colors">{c.t}</h3>
                <p className="mt-2.5 text-sm text-[#44403C] leading-relaxed">{c.d}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CONTROLS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-8 py-20 lg:py-28">
        <Reveal>
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Il controllo qualità</p>
          <h2 className="font-serif-display font-bold text-2xl sm:text-3xl lg:text-4xl text-[#1C1917] tracking-tight max-w-2xl">
            Tre livelli di controllo, dal fornitore alla tua tavola.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-8">
          {CONTROLS.map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <p className="font-mono-accent text-sm text-[#7DC242] mb-5">{String(i + 1).padStart(2, "0")}</p>
              <div className="w-12 h-12 rounded-full bg-[#2E8A57]/10 flex items-center justify-center mb-5">
                <c.icon className="w-5 h-5 text-[#2E8A57]" />
              </div>
              <h3 className="font-serif-display font-semibold text-xl text-[#1C1917]">{c.t}</h3>
              <p className="mt-2.5 text-sm text-[#44403C] leading-relaxed">{c.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#243329] text-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-20 text-center">
          <Reveal>
            <h2 className="font-serif-display font-bold text-3xl sm:text-4xl tracking-tight">Mangia sereno, siamo certificati.</h2>
            <p className="mt-4 text-[#FDFBF7]/70 max-w-lg mx-auto leading-relaxed">
              Scopri i 79 piatti della nostra cucina gluten free, tutti sotto i 20 ppm.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link to="/prodotti" data-testid="cert-cta-btn" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm bg-[#7DC242] text-[#243329] hover:bg-[#6BAB35] transition-colors">
                Scopri i piatti <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/packaging" data-testid="cert-packaging-link" className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-sm border border-[#FDFBF7]/40 hover:bg-[#FDFBF7]/10 transition-colors">
                Il packaging sostenibile
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />
    </div>
  );
}
